import React from 'react';
import { useDarkMode } from '../../hooks/useDarkmode';
import '../../styles/theme.css';

// Support both 'all' and 'overall' for backward compatibility
export type Side = 'all' | 'overall' | 't' | 'ct';

interface SideSelectorProps {
    selectedSide: 'all' | 'overall' | 't' | 'ct';
    onSideChange: (side: 'all' | 'overall' | 't' | 'ct') => void;
    variant?: 'all' | 'overall'; // Allow choosing which label to display
}

const SideSelector: React.FC<SideSelectorProps> = ({ selectedSide, onSideChange, variant = 'all' }) => {
    const isDark = useDarkMode();
    const allLabel = variant === 'overall' ? 'Overall' : 'All';
    const allValue = variant as 'all' | 'overall';
    const isAllActive = selectedSide === 'all' || selectedSide === 'overall';
    
    // Softer, muted colors for selected states
    const tColorActive = 'rgba(240, 173, 78, 0.25)';
    const tColorBorder = 'rgba(240, 173, 78, 0.6)';
    const ctColorActive = 'rgba(91, 192, 222, 0.25)';
    const ctColorBorder = 'rgba(91, 192, 222, 0.6)';
    const allColorActive = isDark ? 'var(--bg-tertiary)' : '#f0f0f0';
    const allColorBorder = 'rgba(241, 92, 37, 0.5)';

    return (
        <div className="btn-group btn-group-sm" role="group" style={{ display: 'flex', flexWrap: 'nowrap' }}>
            <button
                type="button"
                className="btn"
                onClick={() => onSideChange(allValue)}
                style={{
                    backgroundColor: isAllActive
                        ? allColorActive
                        : isDark ? 'var(--bg-primary)' : 'transparent',
                    borderColor: isAllActive
                        ? allColorBorder
                        : isDark ? 'var(--border-primary)' : '#dee2e6',
                    color: isDark ? 'var(--text-primary)' : '#495057',
                    borderRadius: '4px 0 0 4px',
                    fontWeight: isAllActive ? '600' : '400',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    padding: '3px 8px',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    borderWidth: '1px',
                    lineHeight: '1.2'
                }}
                onMouseEnter={(e) => {
                    if (!isAllActive) {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-tertiary)' : '#e9ecef';
                        e.currentTarget.style.borderColor = allColorBorder;
                    }
                }}
                onMouseLeave={(e) => {
                    if (!isAllActive) {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-primary)' : 'transparent';
                        e.currentTarget.style.borderColor = isDark ? 'var(--border-primary)' : '#dee2e6';
                    }
                }}
            >
                {allLabel}
            </button>
            <button
                type="button"
                className="btn"
                onClick={() => onSideChange('t')}
                style={{
                    backgroundColor: selectedSide === 't'
                        ? tColorActive
                        : isDark ? 'var(--bg-primary)' : 'transparent',
                    borderColor: selectedSide === 't'
                        ? tColorBorder
                        : isDark ? 'var(--border-primary)' : '#dee2e6',
                    color: selectedSide === 't'
                        ? '#f0ad4e'
                        : isDark ? 'rgba(240, 173, 78, 0.7)' : '#c49a3a',
                    borderRadius: '0',
                    fontWeight: selectedSide === 't' ? '600' : '400',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    padding: '3px 8px',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    borderWidth: '1px',
                    lineHeight: '1.2'
                }}
                onMouseEnter={(e) => {
                    if (selectedSide !== 't') {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-tertiary)' : '#e9ecef';
                        e.currentTarget.style.borderColor = tColorBorder;
                    }
                }}
                onMouseLeave={(e) => {
                    if (selectedSide !== 't') {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-primary)' : 'transparent';
                        e.currentTarget.style.borderColor = isDark ? 'var(--border-primary)' : '#dee2e6';
                    }
                }}
            >
                T
            </button>
            <button
                type="button"
                className="btn"
                onClick={() => onSideChange('ct')}
                style={{
                    backgroundColor: selectedSide === 'ct'
                        ? ctColorActive
                        : isDark ? 'var(--bg-primary)' : 'transparent',
                    borderColor: selectedSide === 'ct'
                        ? ctColorBorder
                        : isDark ? 'var(--border-primary)' : '#dee2e6',
                    color: selectedSide === 'ct'
                        ? '#5bc0de'
                        : isDark ? 'rgba(91, 192, 222, 0.7)' : '#3a9ab8',
                    borderRadius: '0 4px 4px 0',
                    fontWeight: selectedSide === 'ct' ? '600' : '400',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    padding: '3px 8px',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    borderWidth: '1px',
                    lineHeight: '1.2'
                }}
                onMouseEnter={(e) => {
                    if (selectedSide !== 'ct') {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-tertiary)' : '#e9ecef';
                        e.currentTarget.style.borderColor = ctColorBorder;
                    }
                }}
                onMouseLeave={(e) => {
                    if (selectedSide !== 'ct') {
                        e.currentTarget.style.backgroundColor = isDark ? 'var(--bg-primary)' : 'transparent';
                        e.currentTarget.style.borderColor = isDark ? 'var(--border-primary)' : '#dee2e6';
                    }
                }}
            >
                CT
            </button>
        </div>
    );
};

export default SideSelector;